import { Link, Outlet, useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { Sun, Moon, Search } from 'lucide-react';
import { useSite } from '../hooks/useSite';
import UserAvatarMenu from './UserAvatarMenu';
import NotificationBell from './NotificationBell';

let prefetchedCategories = false;
let prefetchedNewTopic = false;
let prefetchedSearch = false;

function shouldPrefetch(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
  };
  const conn = nav.connection;
  if (!conn) return true;
  if (conn.saveData) return false;
  return conn.effectiveType !== 'slow-2g' && conn.effectiveType !== '2g';
}

export default function Layout() {
  const { user, role, setUser, setRole } = useAuth();
  const { site } = useSite();
  const { theme, toggle: toggleTheme } = useTheme();
  const location = useLocation();
  const categoryMatch = location.pathname.match(/^\/c\/([^/]+)/);
  const newTopicUrl = categoryMatch ? `/new-topic?category=${encodeURIComponent(categoryMatch[1])}` : '/new-topic';

  const prefetchCategories = () => {
    if (!shouldPrefetch()) return;
    if (prefetchedCategories) return;
    prefetchedCategories = true;
    void import('../pages/CategoriesPage');
  };

  const prefetchNewTopic = () => {
    if (!shouldPrefetch()) return;
    if (prefetchedNewTopic) return;
    prefetchedNewTopic = true;
    void import('../pages/NewTopicPage');
  };

  const prefetchSearch = () => {
    if (!shouldPrefetch()) return;
    if (prefetchedSearch) return;
    prefetchedSearch = true;
    void import('../pages/SearchPage');
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 h-12 sm:h-14 flex items-center justify-between">
          <Link to="/" className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 hover:text-primary no-underline flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <span className="text-xl sm:text-2xl">🦉</span>
            <span className="hidden sm:inline truncate max-w-[120px] sm:max-w-none">{site?.name || 'APOO'}</span>
          </Link>
          <nav className="flex items-center gap-1 sm:gap-3 text-sm">
            <Link
              to="/categories"
              onMouseEnter={prefetchCategories}
              onFocus={prefetchCategories}
              className="inline-flex px-2 sm:px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <span className="sm:hidden">Cats</span>
              <span className="hidden sm:inline">Categories</span>
            </Link>
            <Link
              to={newTopicUrl}
              onMouseEnter={prefetchNewTopic}
              onFocus={prefetchNewTopic}
              className="btn-primary text-xs px-2.5 sm:px-3 py-1.5"
            >
              <span className="hidden sm:inline">New Topic</span>
              <span className="sm:hidden">+ New</span>
            </Link>
            <Link to="/search" onMouseEnter={prefetchSearch} onFocus={prefetchSearch} className="p-1.5 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" title="Search">
              <Search size={16} />
            </Link>
            {user && <NotificationBell />}
            <button
              onClick={toggleTheme}
              className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors hidden sm:inline-flex"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {user ? (
              <UserAvatarMenu
                user={user}
                role={role}
                onLogout={async () => { await api.auth.logout().catch(() => {}); setUser(null); setRole(null); window.location.href = '/login'; }}
              />
            ) : (
              <Link to="/login" className="btn-primary text-xs px-2.5 sm:px-3 py-1.5">Log In</Link>
            )}
          </nav>
        </div>
      </header>
      <main className="flex-1 max-w-6xl mx-auto w-full px-3 sm:px-4 py-4 sm:py-6">
        <Outlet />
      </main>
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-4 sm:py-6 mt-auto" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="max-w-6xl mx-auto px-4 text-center text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          Powered by <a href="https://aparliamentofowls.com" className="font-medium">A Parliament of Owls</a>
        </div>
      </footer>
    </div>
  );
}
